import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings } from "../../Redux/WebsiteSlice";
import { useBookingModal } from "../../context/BookingModalContext";
import { contactAPI } from "../../services/api";
import Reveal from "../Shared/Reveal";
import "./ContactPage.scss";

const ContactPage = () => {
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.website.settings);
  const { openModal } = useBookingModal();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Full name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!form.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    setSubmitting(true);
    try {
      await contactAPI.submit(form);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.response?.data?.message || "Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const contact = settings?.contact || {};
  const hours = settings?.openingHours || [];
  const social = settings?.socialMedia || {};

  return (
    <div className="page-contact">
      <div className="contact-body">
        <div className="contact-body__inner">
          {/* Left: Info */}
          <Reveal as="div" direction="left" className="contact-info">
            <h2 className="contact-info__title">Clinic Information</h2>

            {(contact.address?.street || contact.address?.city) && (
              <div className="contact-info__block">
                <div className="contact-info__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <strong>Address</strong>
                  <p>{contact.address?.street}</p>
                  <p>{contact.address?.city}</p>
                </div>
              </div>
            )}

            {contact.phone && (
              <div className="contact-info__block">
                <div className="contact-info__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.13 11.9a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.04 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.04 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z"></path>
                  </svg>
                </div>
                <div>
                  <strong>Phone</strong>
                  <p><a href={`tel:${contact.phone}`}>{contact.phone}</a></p>
                </div>
              </div>
            )}

            {contact.email && (
              <div className="contact-info__block">
                <div className="contact-info__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div>
                  <strong>Email</strong>
                  <p><a href={`mailto:${contact.email}`}>{contact.email}</a></p>
                </div>
              </div>
            )}

            {hours.length > 0 && (
              <div className="contact-hours">
                <h3 className="contact-hours__title">Opening Hours</h3>
                <ul className="contact-hours__list">
                  {hours.map((h, i) => (
                    <li key={i} className={`contact-hours__item ${h.isClosed ? "contact-hours__item--closed" : ""}`}>
                      <span className="contact-hours__day">{h.day}</span>
                      <span className="contact-hours__time">{h.isClosed ? "Closed" : h.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(social.facebook || social.instagram) && (
              <div className="contact-social">
                <h3>Follow Us</h3>
                <div className="contact-social__links">
                  {social.facebook && (
                    <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="contact-social__link">
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                      </svg>
                      Facebook
                    </a>
                  )}
                  {social.instagram && (
                    <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="contact-social__link">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                        <circle cx="12" cy="12" r="4"/>
                        <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor"/>
                      </svg>
                      Instagram
                    </a>
                  )}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => setTimeout(openModal, 0)}
              className="site__button site__button_style--solid site__button_shape--default contact-info__book-btn"
            >
              <span className="text">Book an Appointment</span>
            </button>
          </Reveal>

          {/* Right: Form */}
          <Reveal as="div" direction="right" delay={0.1} className="contact-form-wrap">
            <h2 className="contact-form-wrap__title">Send Us a Message</h2>
            {submitted ? (
              <div className="contact-form-success">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <h3>Message Sent!</h3>
                <p>Thank you for getting in touch. A member of our team will be in contact shortly.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                {submitError && <div className="contact-form__error">{submitError}</div>}
                <div className="contact-form__group">
                  <label htmlFor="name">Full Name <span className="field-required-mark">*</span></label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    className={errors.name ? "error" : ""}
                    placeholder="Your full name"
                  />
                  {errors.name && <span className="field-error-message">{errors.name}</span>}
                </div>
                <div className="contact-form__row">
                  <div className="contact-form__group">
                    <label htmlFor="email">Email Address <span className="field-required-mark">*</span></label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      className={errors.email ? "error" : ""}
                      placeholder="your@email.com"
                    />
                    {errors.email && <span className="field-error-message">{errors.email}</span>}
                  </div>
                  <div className="contact-form__group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="(08) 0000 0000"
                    />
                  </div>
                </div>
                <div className="contact-form__group">
                  <label htmlFor="message">Message <span className="field-required-mark">*</span></label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    value={form.message}
                    onChange={handleChange}
                    className={errors.message ? "error" : ""}
                    placeholder="How can we help you?"
                  />
                  {errors.message && <span className="field-error-message">{errors.message}</span>}
                </div>
                <button type="submit" disabled={submitting} className="site__button site__button_style--solid site__button_shape--default">
                  <span className="text">{submitting ? "Sending..." : "Send Message"}</span>
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>

      {contact.mapEmbedUrl && (
        <div className="contact-map">
          <iframe
            src={contact.mapEmbedUrl}
            title="Clinic location"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </div>
  );
};

export default ContactPage;
